import { syncState } from "@legendapp/state";
import { FivetranConnectionSchema } from "@repo/fivetran/types";
import { toast } from "sonner";

import { trpc } from "~/libs/trpc.lib";
import { getRouter } from "~/router";
import { datasources$ } from "~/stores/datasources";
import { planLimitReasonFromError } from "~/stores/subscription";
import { connections$ } from "./connections.crud";

const inflightDestinations = new Map<string, Promise<void>>();

let inflightManagedProvision: Promise<string | null> | null = null;

/**
 * Provisions (or returns the existing) Mora-managed BigQuery warehouse for the
 * active org. The server creates the datasource row up front and broadcasts it,
 * so `datasources$` picks it up via pubsub; this returns the id so callers can
 * auto-select it. Deduplicates concurrent calls. Returns `null` on failure.
 */
export const provisionManagedDestination = (): Promise<string | null> => {
  if (inflightManagedProvision) return inflightManagedProvision;

  const promise = trpc.connections.provisionManagedDestination
    .mutate()
    .then((result) => result.datasourceId)
    .catch(() => {
      toast.error("Failed to create managed warehouse");
      return null;
    })
    .finally(() => {
      inflightManagedProvision = null;
    });

  inflightManagedProvision = promise;
  return promise;
};

/**
 * Ensures a Fivetran destination is provisioned for the given datasource.
 * Deduplicates concurrent calls for the same datasource and writes the
 * service account email back into the datasources$ store on success.
 */
export const ensureFivetranDestination = (datasourceId: string) => {
  if (!datasourceId) return;

  const existing =
    datasources$[
      datasourceId
    ]?.fivetranDestinations?.serviceAccountEmail?.get();
  if (existing) return;

  const inflight = inflightDestinations.get(datasourceId);
  if (inflight) return inflight;

  const promise = trpc.connections.ensureDestination
    .mutate({ datasourceId })
    .then((result) => {
      datasources$[datasourceId].fivetranDestinations.set({
        serviceAccountEmail: result.serviceAccountEmail,
      });
    })
    .catch(() => {
      toast.error("Failed to provision Fivetran destination");
    })
    .finally(() => {
      inflightDestinations.delete(datasourceId);
    });

  inflightDestinations.set(datasourceId, promise);
  return promise;
};

/** Result of {@link createConnection}: whether it succeeded and, for Connect
 * Card (OAuth) services, the hosted auth URI the caller should open. */
export interface CreateConnectionResult {
  success: boolean;
  connectCardUri?: string | null;
}

/**
 * Create a Fivetran connection against the chosen datasource destination.
 * `datasourceId` must be provided by the caller (selected in the form).
 * `redirectPath` is the relative path the Connect Card returns the user to
 * after OAuth. Returns `{ success, connectCardUri }` — callers use `success` to
 * decide whether to reset, and `connectCardUri` (present only for OAuth
 * services) to open the hosted authentication screen.
 */
export const createConnection = async (
  options: unknown,
  datasourceId: string,
  redirectPath?: string,
): Promise<CreateConnectionResult> => {
  const { name, config, service } = FivetranConnectionSchema.parse(options);

  if (!datasourceId) {
    toast.error("Please select a destination for this connection.");
    return { success: false };
  }

  let result: CreateConnectionResult = { success: false };
  try {
    const { connectCardUri } = await trpc.connections.create.mutate({
      name,
      service,
      config,
      datasourceId,
      redirectPath,
    });
    toast.success("Connection added", {
      description: connectCardUri
        ? "Complete authentication in the new tab to finish setup."
        : "It will start syncing shortly.",
    });
    result = { success: true, connectCardUri };
  } catch (error) {
    // Plan limit hit (free plan disables connectors) — send the user to the
    // Plans page instead of the raw error token.
    if (planLimitReasonFromError(error)) {
      getRouter().navigate({ to: "/settings/plans" });
      await syncState(connections$).sync();
      return { success: false };
    }
    const message =
      error instanceof Error ? error.message : "Error creating connection";
    console.error("Error creating connection", message);
    // Strip the tRPC path prefix (e.g. "connections.create: ") added by the
    // client link so the user sees only the mapped Fivetran message.
    toast.error(message.replace(/^connections\.\w+:\s*/, ""));
  }

  // Refresh the connections list
  await syncState(connections$).sync();

  return result;
};
